import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="design-v2"
//
// Handles theme syncing for the Design V2 stylesheet system. Shares the
// `theme` localStorage key with the legacy theme toggle so the two
// systems agree on dark vs light — previously V2 had its own
// `designV2Theme` key, which meant clicking a V2 page after toggling
// dark mode would silently flip the legacy `data-theme` attribute back
// to "light" and flash the page white.
//
// On connect:
//   1. Ensure <body> has class `design-v2`.
//   2. Read the current theme from html[data-theme] (set by the inline
//      script in application.html.erb), falling back to localStorage and
//      finally "dark" (the app default).
//   3. Mirror data-theme onto <body> too — V2 CSS is scoped as
//      `body.design-v2[data-theme="..."]`, so without this attribute on
//      body, V2 dark mode wouldn't render dark.
//   4. Watch html[data-theme] for changes from the legacy toggle and
//      keep <body> in sync.
//
// toggle() flips the theme on both html and body and persists to
// localStorage["theme"] (shared key) so the legacy toggle sees it.
export default class extends Controller {
    // Shared with the legacy theme toggle.
    static LEGACY_KEY = "theme"
    // Old V2-only key — read for back-compat, never written.
    static OLD_V2_KEY = "designV2Theme"

    connect() {
        document.body.classList.add("design-v2")

        const html = document.documentElement
        const theme = html.getAttribute("data-theme")
            || localStorage.getItem(this.constructor.LEGACY_KEY)
            || localStorage.getItem(this.constructor.OLD_V2_KEY)
            || "dark"

        html.setAttribute("data-theme", theme)
        document.body.setAttribute("data-theme", theme)

        // If the legacy toggle (or anything else) changes html[data-theme],
        // mirror it onto body so V2 CSS selectors keep matching.
        if (this._observer) this._observer.disconnect()
        this._observer = new MutationObserver(() => {
            const next = html.getAttribute("data-theme") || "dark"
            if (document.body.getAttribute("data-theme") !== next) {
                document.body.setAttribute("data-theme", next)
            }
        })
        this._observer.observe(html, { attributes: true, attributeFilter: ["data-theme"] })
    }

    disconnect() {
        if (this._observer) {
            this._observer.disconnect()
            this._observer = null
        }
    }

    toggle(event) {
        if (event) event.preventDefault()

        const html = document.documentElement
        const current = html.getAttribute("data-theme") || "dark"
        const next = current === "dark" ? "light" : "dark"

        html.setAttribute("data-theme", next)
        document.body.setAttribute("data-theme", next)
        if (next === "dark") {
            html.classList.add("dark")
        } else {
            html.classList.remove("dark")
        }
        localStorage.setItem(this.constructor.LEGACY_KEY, next)
    }
}
